import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { installSecurityGuard } from "@/lib/securityGuard";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import BranchSelector from "@/pages/BranchSelector";
import Dashboard from "@/pages/Dashboard";
import PatientRegister from "@/pages/PatientRegister";
import PatientSearch from "@/pages/PatientSearch";
import NurseVitals from "@/pages/NurseVitals";
import DoctorConsultation from "@/pages/DoctorConsultation";
import PublicQueue from "@/pages/PublicQueue";
import DoctorMapping from "@/pages/DoctorMapping";
import ManagerIP from "@/pages/ManagerIP";
import ManagerReports from "@/pages/ManagerReports";
import ManagerTherapists from "@/pages/ManagerTherapists";
import PharmacyBilling from "@/pages/PharmacyBilling";
import PharmacyDispense from "@/pages/PharmacyDispense";
import Inventory from "@/pages/Inventory";
import DirectBill from "@/pages/DirectBill";
import BulkPatientImport from "@/pages/BulkPatientImport";
import InsurancePortal from "@/pages/InsurancePortal";
import UserManagement from "@/pages/UserManagement";
import IPChart from "@/pages/IPChart";
import AccountsBillsReview from "@/pages/AccountsBillsReview";
import SuperadminVault from "@/pages/SuperadminVault";
import MarketingFollowups from "@/pages/MarketingFollowups";

function AuthedRedirect() {
  const { user, branch } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  return <Navigate to={branch ? "/dashboard" : "/select-branch"} replace />;
}

function App() {
  useEffect(() => { installSecurityGuard(); }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<AuthedRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/queue" element={<PublicQueue />} />

            <Route
              path="/select-branch"
              element={
                <ProtectedRoute requireBranch={false}>
                  <BranchSelector />
                </ProtectedRoute>
              }
            />

            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route
                path="/patients/new"
                element={
                  <ProtectedRoute allow={["admin", "superadmin"]}>
                    <PatientRegister />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patients/bulk"
                element={
                  <ProtectedRoute allow={["admin", "manager", "superadmin"]}>
                    <BulkPatientImport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/insurance"
                element={
                  <ProtectedRoute allow={["manager", "ip", "superadmin"]}>
                    <InsurancePortal />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute allow={["superadmin"]}>
                    <UserManagement />
                  </ProtectedRoute>
                }
              />
              <Route path="/patients" element={<PatientSearch />} />
              <Route
                path="/vitals"
                element={
                  <ProtectedRoute allow={["nurse", "superadmin"]}>
                    <NurseVitals />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/consultation"
                element={
                  <ProtectedRoute allow={["doctor", "superadmin"]}>
                    <DoctorConsultation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/doctors"
                element={
                  <ProtectedRoute allow={["superadmin"]}>
                    <DoctorMapping />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing"
                element={
                  <ProtectedRoute allow={["billing", "manager", "ip", "superadmin"]}>
                    <PharmacyBilling />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing/direct"
                element={
                  <ProtectedRoute allow={["billing", "manager", "admin", "superadmin"]}>
                    <DirectBill />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/direct-bills"
                element={
                  <ProtectedRoute allow={["manager", "admin", "superadmin"]}>
                    <AccountsBillsReview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/vault"
                element={
                  <ProtectedRoute allow={["superadmin"]} requireBranch={false}>
                    <SuperadminVault />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/marketing/followups"
                element={
                  <ProtectedRoute allow={["marketing", "manager", "superadmin"]} requireBranch={false}>
                    <MarketingFollowups />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dispense"
                element={
                  <ProtectedRoute allow={["pharmacy", "superadmin"]}>
                    <PharmacyDispense />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/ip"
                element={
                  <ProtectedRoute allow={["manager", "ip", "superadmin"]} feature="ip_admission">
                    <ManagerIP />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/ip/chart/:id"
                element={
                  <ProtectedRoute allow={["manager", "ip", "doctor", "nurse", "superadmin"]} feature="ip_admission">
                    <IPChart />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/reports"
                element={
                  <ProtectedRoute allow={["manager", "admin", "superadmin"]}>
                    <ManagerReports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/therapists"
                element={
                  <ProtectedRoute allow={["manager", "ip", "superadmin"]}>
                    <ManagerTherapists />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory"
                element={
                  <ProtectedRoute allow={["pharmacy", "billing", "manager", "superadmin"]}>
                    <Inventory />
                  </ProtectedRoute>
                }
              />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster
          position="top-right"
          richColors
          closeButton
          toastOptions={{
            style: {
              fontFamily: "Manrope, sans-serif",
              borderRadius: "8px",
            },
          }}
        />
      </AuthProvider>
    </div>
  );
}

export default App;
